import type { Ref, DeepReadonly } from 'vue'
import type { NitroFetchOptions, NitroFetchRequest } from 'nitropack'
import { DateTime } from 'luxon'
import { uniq, flatten } from 'ramda'
import type { Company, IncludeCommodity, IncludeLastQuote, IncludeRegion } from '@/entities/company'
import { DeckBaseAccess, type Deck } from '@/entities/deck'
import type { Asset, AssetRegion, Commodity } from '@/entities/asset'
import type { CompanyActivity } from '@/entities/companyActivity'
import type { Stock } from '@/entities/stock'
import type { CompanyPerson } from '@/entities/companyPerson'
import type { CompanyCapitalStructure } from '@/entities/companyCapitalStructure'
import type { NewsfeedPost } from '@/entities/newsfeedPost'

interface State {
  loading: boolean
  error: Error | { statusCode?: number } | null
  company: (Company & IncludeCommodity & IncludeRegion & IncludeLastQuote) | null
  featuredDeck: Deck | null
  capitalStructure: CompanyCapitalStructure | null
  stocks: Stock[]
  investorDecks: Deck[]
  vrifiedAssets: Asset[]
  otherAssets: Asset[]
  people: CompanyPerson[]
  activities: CompanyActivity[]
  commodities: Commodity[]
  regions: AssetRegion[]
  posts: {
    loading: boolean
    next: string | null
    results: NewsfeedPost[]
  }
}

export interface UseCompany {
  state: DeepReadonly<Ref<State>>
  initialize(): Promise<void>
  fetchNextPosts(): Promise<void>
}

export const POSTS_LIMIT = 3
const initialState = {
  loading: false,
  error: null,
  company: null,
  vrifiedAssets: [],
  otherAssets: [],
  activities: [],
  stocks: [],
  people: [],
  featuredDeck: null,
  investorDecks: [],
  capitalStructure: null,
  commodities: [],
  regions: [],
  posts: {
    loading: false,
    next: null,
    results: [],
  },
}

export function useCompany({ id, omitAuth }: { id: string; omitAuth?: boolean }): UseCompany {
  const config = useRuntimeConfig()

  const state = useState<State>('company', () => initialState)

  async function initialize() {
    try {
      state.value = { ...initialState, loading: true }

      const data = await _wrappedGet<Company & IncludeCommodity & IncludeRegion & IncludeLastQuote>(`v2/companies/${id}`, {
        params: { include: ['last_quote', 'commodity', 'region'] },
      })

      state.value = { ...state.value, company: data }

      await Promise.all([
        _setAssetsRegionsAndCommodities(data),
        _setActivities(data),
        _setStocks(data),
        _setPersons(data),
        _setFeaturedDeck(data),
        _setInvestorDeck(data),
        _setCapitalStructure(data),
        _setPosts(
          `v2/companies/${data.id}/posts?limit=3&offset=0&sort_by=date&sort_direction=desc&include%5B%5D=deck&include%5B%5D=company`,
        ),
      ])
    } catch (e) {
      if (e instanceof Error) {
        state.value = { ...state.value, error: e }
      }
    } finally {
      state.value = { ...state.value, loading: false }
    }
  }

  async function fetchNextPosts(): Promise<void> {
    if (state.value.posts.next) {
      _setPosts(state.value.posts.next)
    }
  }

  // Wrapped version of $fetch that strips auth depending on the omitAuth flag

  const _wrappedGet = async <T>(endPoint: string, params: NitroFetchOptions<NitroFetchRequest> = {}) => {
    if (omitAuth) {
      return $fetch<T>(endPoint, { method: 'GET', baseURL: config.public.API_ENDPOINT, ...params })
    }

    return $fetch<T>(endPoint, { method: 'GET', ...useFetchSharedOptions(), ...params })
  }

  const _setAssetsRegionsAndCommodities = async (company: Company | null): Promise<void> => {
    try {
      const { results } = await _wrappedGet<{ results: Asset[] }>(`v2/companies/${company?.id}/assets`, {
        params: { include: ['decks'], sort_by: 'order', view_access_type: DeckBaseAccess.Public },
      })
      const isVrified = (asset: Asset) => asset.is_vrified
      const not =
        (fn: Function) =>
        (...args: any[]) =>
          !fn(...args)
      state.value = {
        ...state.value,
        vrifiedAssets: results.filter(isVrified),
        otherAssets: results.filter(not(isVrified)),
        commodities: uniq(flatten(results.map((a: Asset) => a.commodities.map(c => c?.commodity)))),
        regions: uniq(flatten(results.map((a: Asset) => a.region))).filter(x => x),
      }
    } catch (e) {
      state.value = {
        ...state.value,
        vrifiedAssets: [],
        otherAssets: [],
        commodities: [],
        regions: [],
      }
    }
  }

  const _setActivities = async (company: Company | null): Promise<void> => {
    try {
      // Limit to 10 for now so we don't get too crazy with it
      const { results } = await _wrappedGet<{ results: CompanyActivity[] }>(`v2/companies/${company?.id}/activities`, {
        params: { end_at__gt: DateTime.now().toISO(), limit: 10 },
      })

      state.value = { ...state.value, activities: results }
    } catch (e) {
      state.value = { ...state.value, activities: [] }
    }
  }

  const _setStocks = async (company: Company | null): Promise<void> => {
    try {
      const { eoddata } = await _wrappedGet<{ eoddata: Stock[] }>(`v2/companies/${company?.id}/quote-history`)
      state.value = { ...state.value, stocks: eoddata.reverse() }
    } catch (e) {
      state.value = { ...state.value, stocks: [] }
    }
  }

  const _setPersons = async (company: Company | null): Promise<void> => {
    try {
      const { results } = await _wrappedGet<{ results: CompanyPerson[] }>(`v2/companies/${company?.id}/persons`)
      state.value = { ...state.value, people: results }
    } catch (e) {
      state.value = { ...state.value, people: [] }
    }
  }

  const _setFeaturedDeck = async (company: Company | null): Promise<void> => {
    try {
      const { results } = await _wrappedGet<{ results: Deck[] }>(`v2/companies/${company?.id}/decks/featured`)
      state.value = { ...state.value, featuredDeck: results[0] ?? null }
    } catch (e) {
      state.value = { ...state.value, featuredDeck: null }
    }
  }

  const _setInvestorDeck = async (company: Company | null): Promise<void> => {
    try {
      const { results } = await _wrappedGet<{ results: Deck[] }>(`v2/companies/${company?.id}/decks`, {
        params: { type_tags: 'investor', view_access_type: DeckBaseAccess.Public },
      })
      state.value = { ...state.value, investorDecks: results }
    } catch (e) {
      state.value = { ...state.value, investorDecks: [] }
    }
  }

  const _setCapitalStructure = async (company: Company | null): Promise<void> => {
    try {
      const data = await _wrappedGet<CompanyCapitalStructure>(`v2/companies/${company?.id}/capital-structure/latest`)
      state.value = { ...state.value, capitalStructure: data }
    } catch (e) {
      state.value = { ...state.value, capitalStructure: null }
    }
  }

  const _setPosts = async (url: string): Promise<void> => {
    try {
      state.value = { ...state.value, posts: { ...state.value.posts, loading: true } }
      const data = await _wrappedGet<{ next: string | null; results: NewsfeedPost[] }>(url)
      state.value = { ...state.value, posts: { loading: false, next: data.next, results: state.value.posts.results.concat(data.results) } }
    } catch (e) {
      state.value = { ...state.value, posts: { ...state.value.posts, loading: false, next: null } }
    } finally {
      state.value = { ...state.value, posts: { ...state.value.posts, loading: false } }
    }
  }

  return {
    state: readonly(state),
    initialize,
    fetchNextPosts,
  }
}
